import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { Card, Icon, Loading, PageHead, Switch, useToast } from '../ui';
import { RestaurantStatus, WEBSITE } from '../components/TopBar';
import { SOUND_TYPES, getSoundType, getSoundVolume, playBeep } from '../utils/sound';

const DAYS = [
  ['mon', 'Pazartesi'], ['tue', 'Salı'], ['wed', 'Çarşamba'], ['thu', 'Perşembe'],
  ['fri', 'Cuma'], ['sat', 'Cumartesi'], ['sun', 'Pazar'],
];

const buildWeek = (d) => {
  const base = { open: d.openTime || '09:00', close: d.closeTime || '22:00', closed: false };
  const w = d.weeklyHours || {};
  return Object.fromEntries(DAYS.map(([k]) => [k, {
    open: w[k]?.open || base.open,
    close: w[k]?.close || base.close,
    closed: w[k]?.closed === true,
  }]));
};

function Settings({ userEmail, onSignOut, soundEnabled, onToggleSound }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [settings, setSettings] = useState({
    name: '', phone: '', address: '', about: '',
    openTime: '09:00', closeTime: '22:00',
    deliveryFee: '', minOrderAmount: '', deliveryTime: '',
    acceptedCards: [],
    weeklyHours: buildWeek({}),
    scheduledOrders: true,
  });
  const [newCard, setNewCard] = useState('');
  const [soundType, setSoundType] = useState(getSoundType);
  const [volume, setVolume] = useState(getSoundVolume);
  const saveLS = (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* yok say */ } };
  const chooseSoundType = (t) => { setSoundType(t); saveLS('orderSoundType', t); playBeep('new', { soundType: t, volume }); };
  const changeVolume = (v) => { setVolume(v); saveLS('orderSoundVolume', v); };

  const update = (patch) => { setSettings((p) => ({ ...p, ...patch })); setDirty(true); };

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'restaurants', RESTAURANT_ID));
      if (snap.exists()) {
        const d = snap.data();
        setSettings({
          name: d.name || '', phone: d.phone || '', address: d.address || '', about: d.about || '',
          openTime: d.openTime || '09:00', closeTime: d.closeTime || '22:00',
          deliveryFee: d.deliveryFee ?? '', minOrderAmount: d.minOrderAmount ?? '', deliveryTime: d.deliveryTime ?? '',
          acceptedCards: d.acceptedCards || [],
          weeklyHours: buildWeek(d),
          scheduledOrders: d.scheduledOrders !== false,
        });
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    const fee = parseFloat(settings.deliveryFee);
    const minOrder = parseFloat(settings.minOrderAmount);
    const time = parseInt(settings.deliveryTime, 10);
    if ((!isNaN(fee) && fee < 0) || (!isNaN(minOrder) && minOrder < 0) || (!isNaN(time) && time < 1)) {
      toast('Ücret ve tutar negatif olamaz; teslimat süresi en az 1 dakika olmalı.', 'error');
      return;
    }
    const badDay = DAYS.find(([k]) => {
      const h = settings.weeklyHours[k];
      return !h.closed && (!h.open || !h.close || h.open === h.close);
    });
    if (badDay) {
      toast(`${badDay[1]} için açılış ve kapanış saatini kontrol edin.`, 'error');
      return;
    }
    if (DAYS.every(([k]) => settings.weeklyHours[k].closed)) {
      toast('En az bir gün açık olmalı.', 'error');
      return;
    }
    const firstOpen = DAYS.map(([k]) => settings.weeklyHours[k]).find((h) => !h.closed);
    setSaving(true);
    try {
      // Not: açık/kapalı durumu burada yazılmaz — üst bardaki anahtar yönetir.
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID), {
        name: settings.name.trim(),
        phone: settings.phone.trim(),
        address: settings.address.trim(),
        about: settings.about.trim(),
        openTime: firstOpen.open,
        closeTime: firstOpen.close,
        weeklyHours: settings.weeklyHours,
        scheduledOrders: settings.scheduledOrders,
        deliveryFee: fee || 0,
        minOrderAmount: minOrder || 0,
        deliveryTime: time || 30,
        acceptedCards: settings.acceptedCards,
      });
      setDirty(false);
      toast('Ayarlar kaydedildi.', 'ok');
    } catch {
      toast('Kaydedilemedi, tekrar deneyin.', 'error');
    }
    setSaving(false);
  };

  const addCard = () => {
    const v = newCard.trim();
    if (!v || settings.acceptedCards.includes(v)) return;
    update({ acceptedCards: [...settings.acceptedCards, v] });
    setNewCard('');
  };

  if (loading) return <div className="k-page"><Loading /></div>;

  const field = (key, label, props = {}) => (
    <label className="k-field">
      <span>{label}</span>
      <input className="k-input" value={settings[key]} onChange={(e) => update({ [key]: e.target.value })} {...props} />
    </label>
  );

  return (
    <div className="k-page" style={{ maxWidth: 920 }}>
      <PageHead desc="Restoran bilgileri, çalışma saatleri ve teslimat koşulları. Değişiklikler uygulamada ve web sitesinde anında görünür.">
        <a className="k-btn k-btn-secondary" href={WEBSITE} target="_blank" rel="noopener noreferrer"><Icon name="external" size={15} />Web sitesini aç</a>
        <button type="button" className="k-btn k-btn-primary" onClick={save} disabled={saving || !dirty}>
          <Icon name="check" size={16} />{saving ? 'Kaydediliyor…' : dirty ? 'Değişiklikleri kaydet' : 'Kaydedildi'}
        </button>
      </PageHead>

      <div style={{ display: 'grid', gap: 16 }}>
        <Card title="Sipariş alma durumu" sub="Kapalıyken uygulama ve web sitesinden yeni sipariş alınmaz. Çalışma saatleri dışında anahtar açık olsa bile sipariş alınmaz.">
          <div className="ks-status"><RestaurantStatus /></div>
        </Card>

        <Card title="Sipariş sesi" sub="Yeni ve iptal edilen siparişlerde çalan uyarı. Bu ayar sadece bu cihazda geçerlidir; mutfaktaki bilgisayarda ayrıca ayarlayın.">
          <div style={{ marginBottom: 14 }}>
            <Switch checked={soundEnabled} onChange={onToggleSound} label={soundEnabled ? 'Ses açık' : 'Ses kapalı'} />
          </div>
          <div className={`ks-sound${soundEnabled ? '' : ' is-off'}`}>
            <div className="k-field" style={{ marginBottom: 14 }}>
              <span>Ses tipi</span>
              <div className="k-seg">
                {SOUND_TYPES.map((t) => (
                  <button key={t.key} type="button" className={soundType === t.key ? 'on' : ''} onClick={() => chooseSoundType(t.key)} disabled={!soundEnabled}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="k-field" style={{ marginBottom: 0 }}>
              <span>Ses seviyesi</span>
              <div className="ks-volume">
                <Icon name="mute" size={16} />
                <input type="range" min="0" max="100" step="5" value={volume} disabled={!soundEnabled}
                  onChange={(e) => changeVolume(Number(e.target.value))}
                  onPointerUp={() => playBeep('new', { soundType, volume })}
                  onKeyUp={() => playBeep('new', { soundType, volume })}
                  aria-label="Ses seviyesi" />
                <Icon name="volume" size={16} />
                <strong className="tnum">%{volume}</strong>
                <button type="button" className="k-btn k-btn-secondary k-btn-sm" disabled={!soundEnabled} onClick={() => playBeep('new', { soundType, volume })}>Sesi dene</button>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Restoran bilgileri" sub="Uygulamada ve web sitesinde görünür.">
          <div className="ks-grid">
            {field('name', 'Restoran adı')}
            {field('phone', 'Telefon', { type: 'tel' })}
            <div className="ks-span">{field('address', 'Adres')}</div>
            <label className="k-field ks-span">
              <span>Hakkımızda</span>
              <textarea className="k-textarea" rows={3} value={settings.about} onChange={(e) => update({ about: e.target.value })} />
            </label>
          </div>
        </Card>

        <Card title="Çalışma saatleri" sub="Her gün için ayrı saat girin. Kapanış açılıştan önceyse (ör. 11:00 – 02:00) gece yarısından sonraya kadar açık sayılır. Bu saatlerin dışında anında sipariş alınmaz.">
          <div className="ks-week">
            {DAYS.map(([k, label]) => {
              const h = settings.weeklyHours[k];
              const setDay = (patch) => update({ weeklyHours: { ...settings.weeklyHours, [k]: { ...h, ...patch } } });
              return (
                <div key={k} className={`ks-day${h.closed ? ' is-closed' : ''}`}>
                  <strong>{label}</strong>
                  <Switch checked={!h.closed} onChange={(v) => setDay({ closed: !v })} label={h.closed ? 'Kapalı' : 'Açık'} />
                  <input className="k-input" type="time" value={h.open} disabled={h.closed} onChange={(e) => setDay({ open: e.target.value })} aria-label={`${label} açılış`} />
                  <span>–</span>
                  <input className="k-input" type="time" value={h.close} disabled={h.closed} onChange={(e) => setDay({ close: e.target.value })} aria-label={`${label} kapanış`} />
                </div>
              );
            })}
          </div>
          <button type="button" className="k-btn k-btn-secondary k-btn-sm" style={{ marginTop: 12 }}
            onClick={() => {
              const mon = settings.weeklyHours.mon;
              update({ weeklyHours: Object.fromEntries(DAYS.map(([k]) => [k, { ...settings.weeklyHours[k], open: mon.open, close: mon.close }])) });
            }}>
            Pazartesi saatlerini tüm günlere uygula
          </button>
        </Card>

        <Card title="İleri saatli sipariş" sub="Açıkken müşteri siparişini en fazla 2 gün sonrasına, çalışma saatleri içindeki bir saate verebilir. Restoran kapalıyken de ileri saate sipariş alınır. Üstteki “Sipariş alma durumu” kapalıysa hiç sipariş alınmaz.">
          <Switch checked={settings.scheduledOrders} onChange={(v) => update({ scheduledOrders: v })} label={settings.scheduledOrders ? 'İleri saatli sipariş açık' : 'İleri saatli sipariş kapalı'} />
        </Card>

        <Card title="Teslimat" sub="Adrese teslimat yalnızca Teslimat bölgeleri sayfasında çizilen bölgelere yapılır; bölge dışındaki müşteri sadece Gel Al seçebilir. Bölgenin kendi ücreti veya minimum tutarı girilmemişse buradakiler kullanılır.">
          <div className="ks-grid three">
            {field('deliveryFee', 'Teslimat ücreti (TL)', { type: 'number', min: 0 })}
            {field('minOrderAmount', 'Minimum sepet (TL)', { type: 'number', min: 0 })}
            {field('deliveryTime', 'Teslimat süresi (dk)', { type: 'number', min: 1 })}
          </div>
        </Card>

        <Card title="Ödeme yöntemleri" sub="Nakit ve kredi kartı her zaman açık. Kabul ettiğiniz yemek kartlarını ekleyin.">
          <div className="ks-chips">
            <span className="ks-chip fixed">Nakit</span>
            <span className="ks-chip fixed">Kredi kartı</span>
            {settings.acceptedCards.map((c) => (
              <span key={c} className="ks-chip">
                {c}
                <button type="button" aria-label={`${c} kaldır`} onClick={() => update({ acceptedCards: settings.acceptedCards.filter((x) => x !== c) })}><Icon name="x" size={13} /></button>
              </span>
            ))}
          </div>
          <div className="ks-add">
            <input className="k-input" placeholder="Örn. Multinet, Sodexo, Ticket" value={newCard} onChange={(e) => setNewCard(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCard()} />
            <button type="button" className="k-btn k-btn-secondary" onClick={addCard}><Icon name="plus" size={15} />Ekle</button>
          </div>
        </Card>

        <AppVersionCard />

        <Card title="Hesap">
          <div className="ks-account">
            <div>
              <div className="ks-muted">Oturum açık hesap</div>
              <strong>{userEmail || '—'}</strong>
            </div>
            <button type="button" className="k-btn k-btn-danger" onClick={onSignOut}><Icon name="logout" size={15} />Çıkış yap</button>
          </div>
        </Card>
      </div>

      <style>{`
        .ks-week { display: grid; gap: 8px; }
        .ks-day { display: grid; grid-template-columns: 110px 120px 1fr auto 1fr; align-items: center; gap: 10px; }
        .ks-day.is-closed strong { opacity: .55; }
        @media (max-width: 640px) { .ks-day { grid-template-columns: 1fr auto; } .ks-day input, .ks-day > span { grid-column: span 1; } }
        .ks-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
        .ks-grid.three { grid-template-columns: repeat(3, 1fr); }
        .ks-span { grid-column: 1 / -1; }
        .ks-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
        .ks-chip { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 6px 0 12px; border-radius: 999px; border: 1px solid var(--k-line-strong); background: #fff; font-size: 13px; font-weight: 600; }
        .ks-chip.fixed { padding-right: 12px; background: var(--k-green-soft); border-color: #C9DCCF; color: var(--k-green-dark); }
        .ks-chip button { width: 22px; height: 22px; border: 0; border-radius: 50%; background: transparent; color: var(--k-muted); display: inline-flex; align-items: center; justify-content: center; }
        .ks-chip button:hover { background: var(--k-red-soft); color: var(--k-red); }
        .ks-add { display: flex; gap: 8px; max-width: 460px; }
        .ks-account { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
        .ks-status .k-status { height: 40px; padding: 0 8px 0 14px; font-size: 14px; }
        .ks-sound.is-off { opacity: .5; }
        .ks-volume { display: flex; align-items: center; gap: 10px; max-width: 520px; }
        .ks-volume input[type=range] { flex: 1; accent-color: var(--k-green); height: 4px; }
        .ks-volume strong { min-width: 44px; font-size: 13.5px; }
        .ks-muted { color: var(--k-muted); font-size: 12.5px; }
        @media (max-width: 760px) { .ks-grid, .ks-grid.three { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}

const VERSION_RE = /^\d+\.\d+\.\d+$/;
const cmpVersion = (a, b) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i += 1) if (pa[i] !== pb[i]) return pa[i] - pb[i];
  return 0;
};

function AppVersionCard() {
  const toast = useToast();
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'appConfig', 'version'))
      .then((snap) => {
        const d = snap.exists() ? snap.data() : {};
        setV({
          latestVersion: d.latestVersion || '1.0.0',
          minVersion: d.minVersion || '1.0.0',
          updateMessage: d.updateMessage || '',
        });
      })
      .catch(() => setV({ latestVersion: '1.0.0', minVersion: '1.0.0', updateMessage: '' }));
  }, []);

  if (!v) return null;

  const save = async () => {
    const latest = v.latestVersion.trim();
    const min = v.minVersion.trim();
    if (!VERSION_RE.test(latest) || !VERSION_RE.test(min)) {
      toast('Sürüm 1.0.0 biçiminde olmalı.', 'error');
      return;
    }
    if (cmpVersion(min, latest) > 0) {
      toast('Zorunlu sürüm, güncel sürümden büyük olamaz.', 'error');
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, 'appConfig', 'version'), {
        latestVersion: latest,
        minVersion: min,
        updateMessage: v.updateMessage.trim(),
      }, { merge: true });
      toast('Sürüm ayarı kaydedildi.', 'ok');
    } catch {
      toast('Kaydedilemedi, tekrar deneyin.', 'error');
    }
    setSaving(false);
  };

  return (
    <Card
      title="Uygulama sürümü"
      sub="Mağazaya yeni sürüm yüklendikten sonra buradan güncelleme uyarısı verin. Eski sürümü kullananlara günde bir kez “Güncelleme mevcut” uyarısı çıkar; zorunlu sürümün altındakiler güncellemeden uygulamayı kullanamaz."
      actions={<button type="button" className="k-btn k-btn-secondary" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>}
    >
      <div className="ks-grid">
        <label className="k-field">
          <span>Mağazadaki güncel sürüm</span>
          <input className="k-input" value={v.latestVersion} placeholder="1.0.0" onChange={(e) => setV({ ...v, latestVersion: e.target.value })} />
        </label>
        <label className="k-field">
          <span>Zorunlu en düşük sürüm</span>
          <input className="k-input" value={v.minVersion} placeholder="1.0.0" onChange={(e) => setV({ ...v, minVersion: e.target.value })} />
        </label>
        <label className="k-field ks-span">
          <span>Güncelleme mesajı (isteğe bağlı)</span>
          <input className="k-input" value={v.updateMessage} placeholder="Daha iyi bir deneyim için lütfen uygulamayı güncelleyin." onChange={(e) => setV({ ...v, updateMessage: e.target.value })} />
        </label>
      </div>
    </Card>
  );
}

export default Settings;
